import { useEffect, useRef, useCallback } from 'react';
import { AnalyticsTrackingService } from '../services/analyticsTrackingService';

/**
 * Custom hook for tracking wizard step analytics
 * 
 * Automatically tracks:
 * - When a step is viewed (start timing)
 * - When a step is completed (end timing, calculate duration)
 * - Total session time
 * - Wizard exits/abandonment
 * 
 * @param {number} currentStep - Current step number (1-indexed)
 * @param {string} stepName - Human-readable name of current step
 * @param {string} sessionId - Unique session ID for this wizard run
 * @returns {Object} Analytics methods
 */
export function useWizardStepAnalytics(currentStep, stepName = '', sessionId = null) {
    const sessionRef = useRef(sessionId || generateSessionId());
    const lastStepRef = useRef(null);

    // Track step view when step changes
    useEffect(() => {
        if (currentStep === lastStepRef.current) return;

        // Complete the previous step if there was one
        if (lastStepRef.current !== null) {
            AnalyticsTrackingService.trackStepComplete(sessionRef.current, lastStepRef.current);
        }

        // Track the new step view
        AnalyticsTrackingService.trackStepView(sessionRef.current, currentStep, stepName);

        // TikTok Pixel Tracking
        if (currentStep === 1 && lastStepRef.current === null) {
            // Only track start on very first load of step 1
            import('../utils/tiktokPixel').then(pixel => {
                pixel.trackWizardStart();
                pixel.trackWizardStep(currentStep, stepName);
            });
        } else {
            // Track subsequent steps
            import('../utils/tiktokPixel').then(pixel => {
                pixel.trackWizardStep(currentStep, stepName);
            });
        }

        lastStepRef.current = currentStep;
    }, [currentStep, stepName]);

    // Track exit on unmount (if wizard not completed)
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (lastStepRef.current !== null) {
                AnalyticsTrackingService.trackWizardExit(
                    sessionRef.current,
                    lastStepRef.current,
                    'page_unload'
                );
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    // Mark wizard as complete (clears session, doesn't track as exit)
    const markComplete = useCallback(() => {
        if (lastStepRef.current !== null) {
            AnalyticsTrackingService.trackStepComplete(sessionRef.current, lastStepRef.current);
        }
        AnalyticsTrackingService.clearSession(sessionRef.current);
        lastStepRef.current = null;
    }, []);

    // Get current session duration
    const getSessionDuration = useCallback(() => {
        return AnalyticsTrackingService.getSessionDuration(sessionRef.current);
    }, []);

    // Get step durations
    const getStepDurations = useCallback(() => {
        return AnalyticsTrackingService.getStepDurations(sessionRef.current);
    }, []);

    // Track custom event
    const trackEvent = useCallback((eventType, eventData = {}) => {
        AnalyticsTrackingService.logEvent({
            user_id: sessionRef.current,
            event_type: eventType,
            event_data: {
                ...eventData,
                step_number: currentStep,
                timestamp: new Date().toISOString()
            }
        });
    }, [currentStep]);

    return {
        sessionId: sessionRef.current,
        markComplete,
        getSessionDuration,
        getStepDurations,
        trackEvent
    };
}

// Generate a unique session ID
function generateSessionId() {
    return `wizard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

export default useWizardStepAnalytics;
